import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  RiArrowRightSLine,
  RiCheckboxCircleFill,
  RiCloseLine,
  RiQuestionLine,
} from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { Combobox } from "~/components/ui/combobox";
import { Picture } from "~/components/ui/picture";
import { Spinner } from "~/components/ui/spinner";
import { Drawer, DrawerClose, DrawerContent } from "~/components/ui/drawer";
import { useAddToEmbeddedCart } from "~/lib/use-embedded-cart";
import {
  type ObdCompatibilityCheckerScrollPositions,
  useObdCompatibilityChecker,
} from "~/components/products/pdp/ObdCompatibilityCheckerContext";
import { imageUrl } from "~/lib/image-url";

const obdLocatorGif1 = imageUrl("/images/products/compatibility/obd-locators/obd-1.gif");
const obdLocatorGif2to4 = imageUrl("/images/products/compatibility/obd-locators/obd-2-4.gif");
const obdLocatorGif5 = imageUrl("/images/products/compatibility/obd-locators/obd-5.gif");
const obdLocatorGif6 = imageUrl("/images/products/compatibility/obd-locators/obd-6.gif");
const obdLocatorGif7 = imageUrl("/images/products/compatibility/obd-locators/obd-7.gif");
const obdLocatorGif8 = imageUrl("/images/products/compatibility/obd-locators/obd-8.gif");

// Plain public/ string path, not a JS import — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const bouncieDriveFrontProfile = imageUrl("/images/products/compatibility/bouncie-drive-front-profile.png");

// Ported from bouncieweb's getGraphicUrl (ObdCompatibility.tsx) — graphics 2-4 share
// one gif, others map 1:1.
const OBD_LOCATOR_GIFS: Record<number, string> = {
  1: obdLocatorGif1,
  2: obdLocatorGif2to4,
  3: obdLocatorGif2to4,
  4: obdLocatorGif2to4,
  5: obdLocatorGif5,
  6: obdLocatorGif6,
  7: obdLocatorGif7,
  8: obdLocatorGif8,
};

const comboboxTriggerClassName = "h-12 p-figma-spacing-md text-figma-body-md-size";

// Each field shows its label (with a required *) only once it has a value — before
// that its placeholder carries the field name.
function VehicleField({ id, label, showLabel, children }: { id: string; label: string; showLabel: boolean; children: React.ReactNode }) {
  return (
    <div className="gap-figma-spacing-sm flex flex-col">
      {showLabel && (
        <label htmlFor={id} className="text-figma-text-body-secondary-alt text-figma-body-sm-size flex items-center gap-0.5 leading-5">
          {label}
          <span aria-hidden className="text-figma-blue-bouncie-blue font-medium">
            *
          </span>
        </label>
      )}
      {children}
    </div>
  );
}

// A ref for one of the drawer's scroll containers that also puts back its saved offset the
// moment the container mounts. Restoring from an effect on `open` doesn't work: Radix's
// Portal renders nothing on its first pass, so the containers don't exist yet when `open`
// flips. Each saved offset is used once and then zeroed, so a container that mounts later
// while the drawer stays open (e.g. the confirmed screen after a new lookup) starts at the top.
function useRestoredScrollRef(
  scrollPositionsRef: React.RefObject<ObdCompatibilityCheckerScrollPositions>,
  key: keyof ObdCompatibilityCheckerScrollPositions,
) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  // Stable identity — a new callback each render would re-run it and keep snapping the
  // container back to the saved offset.
  const setRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el;
      if (!el) return;
      el.scrollTop = scrollPositionsRef.current[key];
      scrollPositionsRef.current[key] = 0;
    },
    [scrollPositionsRef, key],
  );
  return [ref, setRef] as const;
}

// Selection step (Make/Model/Year, ported from the old inline VehicleCompatibilityChecker)
// followed by a result step (compatible → Add to Cart, or uncertain → Let's Chat). All
// state lives in ObdCompatibilityCheckerContext, not here — see that file for why.
export function ObdCompatibilityCheckerDrawer() {
  const { t } = useTranslation("products");
  const addToCart = useAddToEmbeddedCart("bouncieobd");
  const {
    open,
    setOpen,
    make,
    model,
    year,
    step,
    isLoadingResult,
    result,
    isResultFailed,
    isVehicleNotListed,
    makes,
    models,
    years,
    isLoadingModels,
    isLoadingYears,
    selectMake,
    selectModel,
    selectYear,
    confirm,
    reportVehicleNotListed,
    checkAnotherVehicle,
    scrollPositionsRef,
  } = useObdCompatibilityChecker();

  // DrawerContent unmounts on close, so the scroll container's offset is captured on
  // close and put back when it remounts on reopen.
  const [drawerScrollRef, setDrawerScrollRef] = useRestoredScrollRef(scrollPositionsRef, "drawer");

  function handleOpenChange(next: boolean) {
    if (!next) {
      scrollPositionsRef.current = {
        drawer: drawerScrollRef.current?.scrollTop ?? 0,
      };
    }
    setOpen(next);
  }

  const makeId = React.useId();
  const modelId = React.useId();
  const yearId = React.useId();

  const obdLocatorGif = result?.graphic ? OBD_LOCATOR_GIFS[Number(result.graphic)] : undefined;
  // "Vehicle Not Listed?" lands on the same Chat With Us state as an uncertain lookup,
  // and ignores any result left over from an earlier lookup.
  const isCompatible = !isVehicleNotListed && !!result?.compatible;
  // A failed lookup falls back to the same "not sure — let's chat" screen.
  const isUncertain = isVehicleNotListed || isResultFailed || (!!result && !result.compatible);
  const isConfirmed = step === "result" && !isLoadingResult && isCompatible;

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      {/* Mobile: the confirmed screen grows to 90% of the viewport (instead of the
          drawer's default 568px) to make room for the OBD port details below the fold. */}
      <DrawerContent className={cn(isConfirmed && "max-sm:h-[90dvh]")}>
        {/* Close sits above the title, not beside it, and outside the scroll container
            below so it stays pinned while the rest of the drawer scrolls. */}
        <div className="px-figma-padding-padding-l-r-pl-r-16 pt-figma-padding-padding-t-b-pt-b-16 box-content flex h-8 shrink-0 items-center justify-end">
          <DrawerClose className="shrink-0 cursor-pointer" aria-label={t("pdp.obd_compatibility_drawer_close")}>
            <RiCloseLine className="text-figma-icon-secondary size-8" />
          </DrawerClose>
        </div>
        {/* Header + body: the drawer's one scroll container, so everything but the close
            button scrolls as a unit on every screen. */}
        <div ref={setDrawerScrollRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* The title block is inset a further 8px (Figma's Drawer Header padding). */}
          <div className="px-figma-padding-padding-l-r-pl-r-16 pt-figma-spacing-xs flex flex-col">
            <div className="gap-figma-spacing-sm px-figma-padding-padding-l-r-pl-r-8 flex flex-col">
              {/* Mobile uses Figma's Body/XL - Bold (Helvetica Now Text 20px) instead of Title/Sm. */}
              <p className="text-figma-text-body-primary text-figma-titles-sm-size font-display max-sm:text-figma-body-xl-size leading-[1.15] font-bold max-sm:font-sans max-sm:leading-[1.1] max-sm:tracking-[0.01em]">
                {step === "select" || isLoadingResult
                  ? t("pdp.obd_compatibility_drawer_title")
                  : t(isCompatible ? "pdp.obd_compatibility_drawer_confirmed_title" : "pdp.obd_compatibility_drawer_possible_title")}
              </p>
              {step === "select" && <p className="text-figma-text-body-secondary-alt text-figma-body-md-size max-sm:text-figma-body-xs-size leading-[1.15]">{t("pdp.obd_compatibility_drawer_description")}</p>}
            </div>
          </div>

          <div
            className={cn(
              "gap-figma-spacing-md px-figma-padding-padding-l-r-pl-r-16 py-figma-padding-padding-t-b-pt-b-32 flex flex-1 flex-col",
              // Labelled fields sit further apart in Figma (16px vs 12px).
              step === "select" && make && "gap-figma-spacing-lg",
            )}
          >
            {step === "select" && (
              <>
                <VehicleField id={makeId} label={t("pdp.obd_compatibility_drawer_make_placeholder")} showLabel={!!make}>
                  <Combobox
                    id={makeId}
                    sid="make"
                    options={makes}
                    value={make}
                    onValueChange={selectMake}
                    placeholder={t("pdp.obd_compatibility_drawer_make_placeholder")}
                    emptyMessage={t("pdp.obd_compatibility_drawer_no_results")}
                    required
                    className={comboboxTriggerClassName}
                  />
                </VehicleField>

                <VehicleField id={modelId} label={t("pdp.obd_compatibility_drawer_model_placeholder")} showLabel={!!model}>
                  <Combobox
                    id={modelId}
                    sid="model"
                    options={models}
                    value={model}
                    onValueChange={selectModel}
                    placeholder={isLoadingModels ? t("pdp.obd_compatibility_drawer_loading") : t("pdp.obd_compatibility_drawer_model_placeholder")}
                    emptyMessage={t("pdp.obd_compatibility_drawer_no_results")}
                    disabled={!make || isLoadingModels}
                    required
                    className={comboboxTriggerClassName}
                  />
                </VehicleField>

                <VehicleField id={yearId} label={t("pdp.obd_compatibility_drawer_year_placeholder")} showLabel={!!year}>
                  <Combobox
                    id={yearId}
                    sid="year"
                    options={years}
                    value={year}
                    onValueChange={selectYear}
                    placeholder={isLoadingYears ? t("pdp.obd_compatibility_drawer_loading") : t("pdp.obd_compatibility_drawer_year_placeholder")}
                    emptyMessage={t("pdp.obd_compatibility_drawer_no_results")}
                    disabled={!model || isLoadingYears}
                    required
                    className={comboboxTriggerClassName}
                  />
                </VehicleField>

                <div className="flex justify-center p-[10px]">
                  <Button variant="link" size="default" className="text-figma-grey-onyx text-figma-body-md-size h-auto p-0 leading-5 underline" onClick={reportVehicleNotListed}>
                    {t("pdp.obd_compatibility_drawer_not_listed")}
                  </Button>
                </div>
              </>
            )}

            {step === "result" && isLoadingResult && (
              <div className="relative flex-1">
                <div className="absolute top-[33%] left-1/2 -translate-x-1/2">
                  <Spinner size="lg" label={t("pdp.obd_compatibility_drawer_loading")} />
                </div>
              </div>
            )}

            {step === "result" && !isLoadingResult && isCompatible && result && (
              <>
                <p className="text-figma-text-body-secondary text-figma-body-md-size leading-[1.5]">{`${year?.display} ${make?.display} ${model?.display}`}</p>
                <div className="gap-figma-spacing-sm rounded-figma-radius-radius-md px-figma-spacing-sm py-figma-spacing-md flex items-center bg-[rgba(6,137,63,0.08)]">
                  {/* Figma: check icon on the left, vertically centered, with the device
                      starting 40px in so the two overlap slightly (the device runs 4px past
                      this 135px frame, as in Figma). */}
                  <div className="relative h-[72px] w-[135px] shrink-0">
                    <div className="absolute top-1 left-10 h-16 w-[99px] overflow-hidden">
                      <Picture
                        src={bouncieDriveFrontProfile}
                        alt=""
                        className="absolute top-[-28.56%] left-[-23.84%] h-[149.1%] w-[147.56%] max-w-none"
                      />
                    </div>
                    <RiCheckboxCircleFill className="text-figma-semantic-feedback-states-success-success-bold absolute top-1/2 left-0 size-12 -translate-y-1/2" />
                  </div>
                  <p className="text-figma-text-body-primary text-figma-titles-xs-size font-display max-w-[223px] min-w-0 leading-[1.15] font-bold">
                    {t("pdp.obd_compatibility_drawer_success_message")}
                  </p>
                </div>
                {/* Figma's Button Block: 24px below the success message (12px margin on top of
                    the parent's 12px gap), 16px between Add to Cart and the always-underlined link. */}
                <div className="mt-figma-spacing-md gap-figma-spacing-lg flex flex-col items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={() => {
                      addToCart();
                      handleOpenChange(false);
                    }}
                  >
                    {t("pdp.obd_compatibility_drawer_add_to_cart")}
                  </Button>
                  <Button variant="link" size="sm" className="text-figma-grey-onyx text-figma-body-md-size leading-5 underline" onClick={checkAnotherVehicle}>
                    {t("pdp.obd_compatibility_drawer_check_another")}
                  </Button>
                </div>

                {/* No location from the API → no "Where's my OBD port?" section (or the divider
                    above it). */}
                {result.description && (
                  <>
                    {/* 24px either side, per Figma (12px margins on top of the parent's 12px gap). */}
                    <hr className="border-figma-button-border-subtle my-figma-spacing-md shrink-0 border-t-[0.5px]" />

                    {/* Always shown, per Figma — no longer a collapsible accordion. */}
                    <div>
                      <h3 className="text-figma-text-headers-primary text-figma-body-md-size py-figma-padding-padding-t-b-pt-b-16 font-bold">
                        {t("pdp.obd_compatibility_drawer_obd_port_question")}
                      </h3>
                      <div className="gap-figma-spacing-lg pb-figma-spacing-sm flex flex-col">
                        {/* API returns this ALL CAPS (same convention as make/model) — lowercased
                            into the "Your OBD port can be found …" lead-in, matching Figma and
                            bouncieweb's ported version. */}
                        <p className="text-figma-text-body-secondary text-figma-body-sm-size leading-5">
                          {t("pdp.obd_compatibility_drawer_obd_port_location", { location: result.description.toLowerCase() })}
                        </p>
                        {obdLocatorGif && (
                          <div className="rounded-figma-radius-radius-3xl h-[235px] w-full overflow-hidden">
                            {/* eslint-disable-next-line local/no-raw-img -- animated GIF: Picture's AVIF/WebP/srcset don't apply */}
                            <img src={obdLocatorGif} alt="" className="size-full object-cover" />
                          </div>
                        )}
                        {result.image && (
                          <div className="rounded-figma-radius-radius-2xl gap-figma-spacing-3xl p-figma-spacing-lg pb-figma-padding-padding-t-b-pt-b-32 bg-figma-background-surface-bg-primary flex flex-col drop-shadow-[0_4px_4px_rgba(0,0,0,0.1)]">
                            <div className="rounded-figma-radius-radius-3xl h-[213px] w-full overflow-hidden">
                              {/* eslint-disable-next-line local/no-raw-img -- external vehicle image URL from the compatibility API */}
                              <img src={result.image} alt="" className="size-full object-cover" />
                            </div>
                            <p className="text-figma-text-body-secondary-alt text-figma-body-xs-size text-center leading-[18px]">
                              {t("pdp.obd_compatibility_drawer_obd_images_caption")}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {step === "result" && !isLoadingResult && isUncertain && (
              <>
                {!isVehicleNotListed && (
                  <p className="text-figma-text-body-secondary text-figma-body-md-size leading-[1.5]">{`${year?.display} ${make?.display} ${model?.display}`}</p>
                )}
                <div className="gap-figma-spacing-md rounded-figma-radius-radius-md px-figma-padding-padding-l-r-pl-r-8 py-figma-spacing-md bg-figma-cards-yellow/7 flex items-start">
                  <RiQuestionLine className="text-figma-cards-yellow size-12 shrink-0" />
                  <p className="text-figma-text-body-primary text-figma-titles-xs-size font-display w-[330px] leading-[1.15] font-bold">
                    {t("pdp.obd_compatibility_drawer_uncertain_message")}
                  </p>
                </div>
                {/* Same Button Block as the confirmed screen: 24px below the message card,
                    16px between Let's Chat and the always-underlined link. */}
                <div className="mt-figma-spacing-md gap-figma-spacing-lg flex flex-col items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    iconRight={<RiArrowRightSLine className="size-6" />}
                    onClick={() => {
                      // TODO: Wire up real Intercom — see bouncieweb's useIntercom()/Intercom.tsx for the
                      // reference implementation. react-use-intercom isn't installed in this app yet.
                      alert("TODO: Implement Intercom");
                    }}
                  >
                    {t("pdp.obd_compatibility_drawer_chat_cta")}
                  </Button>
                  <Button variant="link" size="sm" className="text-figma-grey-onyx text-figma-body-md-size leading-5 underline" onClick={checkAnotherVehicle}>
                    {t("pdp.obd_compatibility_drawer_start_over")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {step === "select" && (
          <div className="border-figma-button-border-subtle gap-figma-spacing-lg px-figma-padding-padding-l-r-pl-r-16 py-figma-padding-padding-t-b-pt-b-24 flex flex-col items-center border-t-[0.5px]">
            {/* Figma: size L on desktop, M (default) on mobile — size isn't responsive, so
                mobile re-applies the default size's classes. */}
            <Button
              variant="primary"
              size="lg"
              className="w-full max-sm:h-[50px] max-sm:py-4 max-sm:text-sm"
              disabled={!make || !model || !year}
              onClick={confirm}
            >
              {t("pdp.obd_compatibility_drawer_confirm_cta")}
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
